import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Film, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const MULTIPLAI_CLIENT_ID = "a14fc918-adc8-4e69-9bd9-9783d5139f94";
const MULTIPLAI_BASE_URL = "https://app.multiplai.tech";
const MULTIPLAI_REDIRECT_URI = "https://property-walkthrough-automation.multiplai.tech/auth/callback";

export default function SignIn() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleMultiplaiLogin = () => {
    const params = new URLSearchParams({
      response_type: "code",
      client_id: MULTIPLAI_CLIENT_ID,
      redirect_uri: MULTIPLAI_REDIRECT_URI,
    });
    window.location.href = `${MULTIPLAI_BASE_URL}/oauth/authorize?${params.toString()}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid credentials");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex" data-testid="signin-page">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-[45%] bg-[#020B23] flex-col justify-between p-12 relative overflow-hidden">
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-16">
            <div className="w-10 h-10 rounded-xl bg-[#4D11F6] flex items-center justify-center">
              <Film className="w-6 h-6 text-white" strokeWidth={1.5} />
            </div>
            <span className="text-white font-bold text-2xl" style={{ fontFamily: 'Sora, sans-serif' }}>
              Multiplai
            </span>
          </div>
          <h1 className="text-white text-4xl font-bold leading-tight mb-6" style={{ fontFamily: 'Sora, sans-serif' }}>
            Turn property listings<br />into walkthrough videos
          </h1>
          <p className="text-[#94969C] text-lg leading-relaxed max-w-md">
            AI-powered storyboard generation, per-shot clip control, and one-click final render.
          </p>
        </div>
        {/* Decorative elements */}
        <div className="absolute top-1/2 right-0 w-96 h-96 bg-[#4D11F6]/10 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#7A81E9]/10 rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2" />
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[#F3F3F9]">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <div className="w-9 h-9 rounded-xl bg-[#4D11F6] flex items-center justify-center">
              <Film className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <span className="text-[#020B23] font-bold text-xl" style={{ fontFamily: 'Sora, sans-serif' }}>
              Multiplai
            </span>
          </div>

          <h2 className="text-3xl font-bold text-[#020B23] mb-2" style={{ fontFamily: 'Sora, sans-serif' }}>
            Sign in
          </h2>
          <p className="text-[#676970] mb-8">
            Enter your credentials to access your workspace
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#1A2238] font-medium">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="signin-email-input"
                className="h-11 bg-white border-[#E2E4E7] focus:border-[#4D11F6] focus:ring-2 focus:ring-[#4D11F6]/20"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#1A2238] font-medium">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="signin-password-input"
                className="h-11 bg-white border-[#E2E4E7] focus:border-[#4D11F6] focus:ring-2 focus:ring-[#4D11F6]/20"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="signin-submit-btn"
              className="w-full h-11 bg-[#4D11F6] hover:bg-[#3A0BC0] text-white font-medium rounded-lg transition-colors"
            >
              {loading ? "Signing in..." : "Sign in"}
              {!loading && <ArrowRight size={16} className="ml-2" />}
            </Button>
          </form>

          <div className="mt-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-[#E2E4E7]" />
            <span className="text-xs text-[#94969C]">or</span>
            <div className="flex-1 h-px bg-[#E2E4E7]" />
          </div>

          <button
            type="button"
            onClick={handleMultiplaiLogin}
            className="mt-4 w-full h-11 flex items-center justify-center gap-3 border border-[#E2E4E7] bg-white hover:bg-[#F3F3F9] rounded-lg transition-colors text-sm font-medium text-[#1A2238]"
          >
            <img src="https://assets.multiplai.tech/img/multiplai_logomark.svg" alt="Multiplai" className="w-5 h-5" />
            Continue with Multiplai
          </button>

          <p className="mt-6 text-center text-sm text-[#676970]">
            No account?{" "}
            <Link to="/signup" className="text-[#4D11F6] font-medium hover:underline" data-testid="goto-signup-link">
              Create one
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
